import PropTypes from "prop-types";
import { useMemo } from "react";
import {
  Link,
  Navigate,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Spinner } from "@/components/ui/spinner";
import ACL_RELATIONSHIPS from "acl-relationships";
import {
  SectionCard,
  useCustomFieldConfigs,
  useCustomFieldGroups,
} from "components/BaseForm";
import DetailHeaderBar from "components/DetailHeaderBar";
import { DetailBreadcrumb } from "components/sidebar/DetailBreadcrumb";
import useAuthorized from "hooks/useAuthorized";
import usePageTitle from "hooks/usePageTitle";
import { useRegionCountry } from "hooks/useRegionCountry";
import { useRevenueOpportunity } from "hooks/useRevenueOpportunity";
import {
  BREADCRUMB_LABELS,
  MODEL_TYPE,
  PAGE_TITLES,
} from "routes/revenue_opportunity/form/constants";
import { FORM_MODES } from "routes/revenue_opportunity/form/defaults";
import LockStateToggle from "routes/revenue_opportunity/form/LockStateToggle";
import RevenueOpportunityForm from "routes/revenue_opportunity/form/RevenueOpportunityForm";
import { useInvoiceProducts } from "routes/revenue_opportunity/form/sections/invoices/useInvoiceProducts";
import { useCloneTags } from "routes/revenue_opportunity/form/useCloneTags";
import { usePresetRecords } from "routes/revenue_opportunity/form/usePresetRecords";
import {
  ENTITY_ORIGINS,
  readCreatePreset,
  revenueOpportunityPaths,
} from "routes/revenue_opportunity/paths";
import UserService from "services/User";

const CenteredMessage = ({ children }) => (
  <div className="tw:flex tw:w-full tw:flex-col tw:items-center tw:gap-3 tw:p-8 tw:text-center tw:text-(--color-text-muted)">
    {children}
  </div>
);

CenteredMessage.propTypes = { children: PropTypes.node };

const BackToList = () => (
  <Link to={revenueOpportunityPaths.list}>Back to Revenue Opportunities</Link>
);

const RECORD_LABEL = id => `Revenue opportunity (${id})`;

/**
 * The page chrome: the same header bar as the record page above a scrolling
 * body. The breadcrumb links back to the list and, on edit and clone, to the
 * source record.
 */
const PageShell = ({ mode, sourceId, children }) => (
  <div className="tw:flex tw:min-h-0 tw:flex-1 tw:flex-col tw:font-sans tw:text-(--color-text-primary)">
    <DetailHeaderBar
      breadcrumb={
        <DetailBreadcrumb
          parentTo={revenueOpportunityPaths.list}
          parentLabel="All Revenue Opportunities"
          trail={
            sourceId
              ? [
                  {
                    to: revenueOpportunityPaths.detail(sourceId),
                    label: RECORD_LABEL(sourceId),
                  },
                ]
              : []
          }
          current={BREADCRUMB_LABELS[mode]}
        />
      }
      title={PAGE_TITLES[mode]}
    />
    {/* The gutter stays reserved so the form does not shift sideways as the
        scrollbar comes and goes. */}
    <div className="tw:min-h-0 tw:flex-1 tw:overflow-y-auto tw:p-4 tw:scrollbar-gutter-stable">
      <div className="tw:mx-auto tw:flex tw:w-full tw:max-w-7xl tw:flex-col tw:gap-4">
        {children}
      </div>
    </div>
  </div>
);

PageShell.propTypes = {
  mode: PropTypes.oneOf(Object.values(FORM_MODES)).isRequired,
  sourceId: PropTypes.string,
  children: PropTypes.node,
};

const resolveMode = (id, cloneId) => {
  if (id) return FORM_MODES.edit;
  return cloneId ? FORM_MODES.clone : FORM_MODES.create;
};

/**
 * The country codes configured for a region, or null while the region is
 * unknown. An unresolved region must not be read as "no countries", or a valid
 * saved country would be cleared.
 */
const useRegionCountryCodes = regionId => {
  const { regions, countries, isFetched } = useRegionCountry({ regionId });
  const isResolved = regions.some(region => region.value === regionId);
  return {
    isFetched,
    regionCountries: isResolved
      ? countries.map(country => country.value)
      : null,
  };
};

/**
 * The full-page create, edit, and clone form. Create and clone share the
 * `new` route; `?clone=<id>` picks the source record.
 */
const RevenueOpportunityFormPage = () => {
  // Only the edit route has an :id segment.
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  const cloneId = searchParams.get("clone");
  const mode = resolveMode(id, cloneId);
  const isEdit = mode === FORM_MODES.edit;
  const isCreate = mode === FORM_MODES.create;
  const sourceId = id ?? cloneId;
  const isAdmin = UserService.isAdmin();
  const returnTo = location.state?.from ?? revenueOpportunityPaths.list;

  const canWrite = useAuthorized([
    isEdit
      ? ACL_RELATIONSHIPS.revenueOpportunity.edit
      : ACL_RELATIONSHIPS.revenueOpportunity.create,
  ]);

  const preset = useMemo(
    () => (isCreate ? readCreatePreset(searchParams) : {}),
    [isCreate, searchParams]
  );

  const {
    data: record,
    isSuccess: isRecordLoaded,
    isFetchedAfterMount,
    isError: isRecordError,
    error: recordError,
  } = useRevenueOpportunity(sourceId);
  // isFetchedAfterMount, not just isSuccess: a cached entry resolves
  // synchronously, and the form latches its defaults at the first render, so
  // mounting on it would save stale values over a newer edit.
  const isRecordReady = !sourceId || (isRecordLoaded && isFetchedAfterMount);

  const configsQuery = useCustomFieldConfigs(MODEL_TYPE);
  const groupsQuery = useCustomFieldGroups(MODEL_TYPE);
  const productsQuery = useInvoiceProducts(record?.invoices, {
    enabled: isRecordReady,
  });
  const presetQuery = usePresetRecords(preset);
  const presetEntity = presetQuery.records.entity;
  const entityPrefill = useMemo(
    () =>
      ENTITY_ORIGINS.includes(preset.origin) && presetEntity
        ? { region_id: presetEntity.region_id, country: presetEntity.country }
        : undefined,
    [preset.origin, presetEntity]
  );
  const cloneTagsState = useCloneTags({
    isClone: mode === FORM_MODES.clone,
    isAdmin,
    sourceTags: record?.tags,
  });
  const regionQuery = useRegionCountryCodes(
    record?.region_id ?? entityPrefill?.region_id ?? null
  );

  usePageTitle(PAGE_TITLES[mode]);

  const handleSaved = saved =>
    navigate(revenueOpportunityPaths.detail(saved.id));
  const handleCancel = () => navigate(returnTo);

  if (!canWrite) {
    return <Navigate to={revenueOpportunityPaths.list} replace />;
  }

  if (isEdit && record?.is_locked && !isAdmin) {
    return <Navigate to={revenueOpportunityPaths.detail(id)} replace />;
  }

  const shell = content => (
    <PageShell mode={mode} sourceId={sourceId}>
      {content}
    </PageShell>
  );

  if (isRecordError) {
    const isNotFound = recordError?.response?.status === 404;
    return shell(
      <CenteredMessage>
        <p>
          {isNotFound
            ? "This Revenue Opportunity does not exist."
            : "Could not load this Revenue Opportunity. Please try again."}
        </p>
        <BackToList />
      </CenteredMessage>
    );
  }

  if (cloneTagsState.isError) {
    return shell(
      <CenteredMessage>
        <p>Could not load your tags, so the clone cannot be prepared.</p>
        <BackToList />
      </CenteredMessage>
    );
  }

  if (configsQuery.isError || productsQuery.isError) {
    return shell(
      <CenteredMessage>
        <p>Could not load the form. Please try again.</p>
        <BackToList />
      </CenteredMessage>
    );
  }

  const isLoading =
    !isRecordReady ||
    configsQuery.isPending ||
    groupsQuery.isPending ||
    productsQuery.isPending ||
    presetQuery.isPending ||
    cloneTagsState.isPending ||
    !regionQuery.isFetched;

  if (isLoading) {
    return shell(
      <CenteredMessage>
        <Spinner />
      </CenteredMessage>
    );
  }

  return shell(
    <>
      {cloneTagsState.droppedTags.length > 0 && (
        <Alert>
          <AlertDescription>
            These tags were not carried over because your role cannot apply
            them: {cloneTagsState.droppedTags.map(tag => tag.name).join(", ")}.
          </AlertDescription>
        </Alert>
      )}
      {isEdit && isAdmin && (
        <SectionCard title="Lock">
          <LockStateToggle
            revenueOpportunityId={record.id}
            isLocked={Boolean(record.is_locked)}
          />
        </SectionCard>
      )}
      <RevenueOpportunityForm
        mode={mode}
        record={record}
        preset={preset}
        presetRecords={presetQuery.records}
        entityPrefill={entityPrefill}
        regionCountries={regionQuery.regionCountries}
        customFieldConfigs={configsQuery.data ?? []}
        customFieldGroups={groupsQuery.data ?? []}
        invoiceProducts={productsQuery.data}
        cloneTags={cloneTagsState.cloneTags}
        onSaved={handleSaved}
        onCancel={handleCancel}
      />
    </>
  );
};

export default RevenueOpportunityFormPage;
